/**
 * @theme/focus — focus traps for overlays that can't use native <dialog>.
 *
 * Most modals in this theme are <dialog> + showModal() which provides a focus trap automatically.
 * These helpers exist for the rare overlay (e.g., a slide-in mobile menu rendered as a custom drawer)
 * that needs a manual trap.
 *
 * Usage:
 *   const release = trapFocus(myDrawer, { initialFocus: closeButton });
 *   // ...later
 *   release();
 *   // OR
 *   releaseFocusTrap(myDrawer);
 */

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'button:not([disabled])',
  'iframe',
  'object',
  'embed',
  '[tabindex]:not([tabindex^="-"])',
  '[contenteditable="true"]',
  'audio[controls]',
  'video[controls]',
  'details > summary:first-of-type',
].join(',');

/** @type {WeakMap<Element, { handler: (e: KeyboardEvent) => void, returnFocus: HTMLElement | null }>} */
const __traps = new WeakMap();

/**
 * @param {Element} container
 * @returns {HTMLElement[]}
 */
export function getFocusableElements(container) {
  const list = container.querySelectorAll(FOCUSABLE_SELECTOR);
  /** @type {HTMLElement[]} */
  const out = [];
  for (const el of list) {
    if (!(el instanceof HTMLElement)) continue;
    if (el.hasAttribute('inert')) continue;
    // Skip elements inside an inert subtree.
    if (el.closest('[inert]')) continue;
    if (el.offsetParent === null && getComputedStyle(el).position !== 'fixed') continue;
    out.push(el);
  }
  return out;
}

/**
 * Trap focus inside `container`. Returns a disposer.
 *
 * @param {Element} container
 * @param {{ initialFocus?: HTMLElement | null, returnFocus?: HTMLElement | null }} [opts]
 * @returns {() => void}
 */
export function trapFocus(container, opts = {}) {
  if (__traps.has(container)) {
    return () => releaseFocusTrap(container);
  }

  const returnFocus = opts.returnFocus ?? /** @type {HTMLElement | null} */ (document.activeElement);

  /** @param {KeyboardEvent} event */
  const handler = (event) => {
    if (event.key !== 'Tab') return;
    const items = getFocusableElements(container);
    if (items.length === 0) {
      event.preventDefault();
      /** @type {HTMLElement} */ (container).focus({ preventScroll: true });
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (event.shiftKey) {
      if (active === first || !container.contains(/** @type {Node} */ (active))) {
        event.preventDefault();
        last.focus();
      }
    } else {
      if (active === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  container.addEventListener('keydown', /** @type {EventListener} */ (handler));

  // Initial focus.
  const initial = opts.initialFocus
    ?? getFocusableElements(container)[0]
    ?? /** @type {HTMLElement} */ (container);
  // Defer one frame so dialog/CSS open transitions don't fight with focus().
  requestAnimationFrame(() => {
    if (!__traps.has(container)) return;
    /** @type {HTMLElement} */ (initial).focus({ preventScroll: false });
  });

  __traps.set(container, { handler: /** @type {any} */ (handler), returnFocus });
  return () => releaseFocusTrap(container);
}

/**
 * @param {Element} container
 */
export function releaseFocusTrap(container) {
  const trap = __traps.get(container);
  if (!trap) return;
  container.removeEventListener('keydown', /** @type {EventListener} */ (trap.handler));
  __traps.delete(container);
  if (trap.returnFocus && document.contains(trap.returnFocus)) {
    try { trap.returnFocus.focus({ preventScroll: false }); } catch { /* element gone */ }
  }
}

/**
 * Cycle to the next focusable element inside container (Tab semantics, but invokable).
 * @param {Element} container
 * @param {-1 | 1} [direction=1]
 */
export function cycleFocus(container, direction = 1) {
  const items = getFocusableElements(container);
  if (!items.length) return;
  const active = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const idx = active ? items.indexOf(active) : -1;
  const next = items[(idx + direction + items.length) % items.length] || items[0];
  next.focus();
}
